import { cn } from '#app/utils/misc'
import { siteContent } from '#app/utils/site/content'
import type { TocEntry } from '#app/utils/site/legal'

const FOCUS_RING =
	'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink'
const LINK = cn(
	'block rounded-[10px] px-2.5 text-[14px] leading-[1.35] text-tt-muted transition-colors duration-200 hover:bg-tt-inset hover:text-tt-ink',
	FOCUS_RING,
)
const ACTIVE =
	'bg-tt-tile-green font-semibold text-tt-ink hover:bg-tt-tile-green'
/** tt-muted, not tt-faint: 12px text needs 4.5:1 on the white card. */
const EYEBROW =
	'text-[12px] font-semibold uppercase tracking-[.12em] text-tt-muted'

function TocList({
	entries,
	activeIndex,
	linkClassName,
}: {
	entries: readonly TocEntry[]
	activeIndex: number
	/** Vertical padding: the reference's 7px in the card, a taller tap target in the compact list. */
	linkClassName: string
}) {
	return (
		<ul className="-mx-2.5 m-0 list-none p-0">
			{entries.map((entry, index) => {
				const active = index === activeIndex
				return (
					<li key={entry.id}>
						<a
							href={entry.href}
							aria-current={active ? 'location' : undefined}
							className={cn(LINK, linkClassName, active && ACTIVE)}
						>
							{entry.label}
						</a>
					</li>
				)
			})}
		</ul>
	)
}

/**
 * The table of contents, one link per section, the one being read lit in
 * the green tint. Two shapes: the sticky `card` beside the prose from 981px
 * up (the reference's .lg__toc), and the folding `compact` list above the
 * prose on narrower screens, where the reference simply dropped it.
 */
export function LegalToc({
	variant,
	entries,
	activeIndex,
}: {
	variant: 'card' | 'compact'
	entries: readonly TocEntry[]
	activeIndex: number
}) {
	const label = siteContent.legal.onThisPage
	if (variant === 'card') {
		return (
			<nav
				aria-label={label}
				className="sticky top-[104px] hidden max-h-[calc(100vh-128px)] self-start overflow-auto rounded-[22px] border border-tt-line-soft bg-white px-5 py-[22px] shadow-tt-soft min-[981px]:block"
			>
				<p className={cn('mb-3', EYEBROW)}>{label}</p>
				<TocList
					entries={entries}
					activeIndex={activeIndex}
					linkClassName="py-[7px]"
				/>
			</nav>
		)
	}
	return (
		<details className="tt-legal-toc-compact mb-7 rounded-[18px] border border-tt-line-soft bg-white shadow-tt-soft min-[981px]:hidden">
			<summary
				className={cn(
					'flex cursor-pointer list-none items-center justify-between gap-3 rounded-[18px] px-5 py-4',
					EYEBROW,
					FOCUS_RING,
				)}
			>
				{label}
				<svg
					className="h-4 w-4 text-tt-muted"
					viewBox="0 0 16 16"
					fill="none"
					stroke="currentColor"
					strokeWidth={1.8}
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<path d="M3.5 6l4.5 4.5L12.5 6" />
				</svg>
			</summary>
			<nav aria-label={label} className="px-5 pb-3">
				<TocList
					entries={entries}
					activeIndex={activeIndex}
					linkClassName="py-2.5"
				/>
			</nav>
		</details>
	)
}
